import { useEffect, useState } from 'react';

const tintableSvgSet = new Set();
const svgMarkupCache = new Map();
const svgMarkupPromises = new Map();

function isBrowserEnvironment() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function sanitizeSvgMarkup(raw) {
  if (!isBrowserEnvironment() || !raw) return raw;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(raw, 'image/svg+xml');
    const svgEl = doc.documentElement;

    if (!svgEl || svgEl.nodeName.toLowerCase() !== 'svg') {
      return raw;
    }

    svgEl.removeAttribute('width');
    svgEl.removeAttribute('height');
    svgEl.setAttribute('width', '100%');
    svgEl.setAttribute('height', '100%');
    if (!svgEl.getAttribute('preserveAspectRatio')) {
      svgEl.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    }

    svgEl.querySelectorAll('script').forEach((node) => node.remove());

    const applyCurrentColor = (node) => {
      const fill = node.getAttribute('fill');
      if (fill && fill.toLowerCase() !== 'none') {
        node.setAttribute('fill', 'currentColor');
      }
      const stroke = node.getAttribute('stroke');
      if (stroke && stroke.toLowerCase() !== 'none') {
        node.setAttribute('stroke', 'currentColor');
      }
    };

    applyCurrentColor(svgEl);
    svgEl.querySelectorAll('*').forEach(applyCurrentColor);

    return svgEl.outerHTML;
  } catch (error) {
    console.warn('[PRE-LOAD SYSTEM] Failed to sanitize SVG markup', error);
    return raw;
  }
}

function decodeDataUri(src) {
  try {
    const [, dataPart = ''] = src.split(',');
    const isBase64 = /^data:image\/svg\+xml;base64,/i.test(src);
    return isBase64 ? atob(dataPart) : decodeURIComponent(dataPart);
  } catch (error) {
    console.warn('[PRE-LOAD SYSTEM] Failed to decode SVG data URI', error);
    return null;
  }
}

async function fetchSvgMarkup(src) {
  if (!isBrowserEnvironment() || !src) return null;

  if (src.startsWith('data:image/svg+xml')) {
    return decodeDataUri(src);
  }

  try {
    const response = await fetch(src, { cache: 'force-cache' });
    if (!response.ok) {
      console.warn('[PRE-LOAD SYSTEM] Failed to fetch SVG markup', src, response.status);
      return null;
    }
    return await response.text();
  } catch (error) {
    console.warn('[PRE-LOAD SYSTEM] Fetch error while loading SVG markup', src, error);
    return null;
  }
}

export function registerTintableSvg(src) {
  if (src) {
    tintableSvgSet.add(src);
  }
}

export function isTintableSvg(src) {
  return Boolean(src) && tintableSvgSet.has(src);
}

export function preloadTintableSvg(src) {
  if (!isTintableSvg(src) || svgMarkupCache.has(src)) {
    return Promise.resolve(svgMarkupCache.get(src) ?? null);
  }

  if (svgMarkupPromises.has(src)) {
    return svgMarkupPromises.get(src);
  }

  const promise = fetchSvgMarkup(src)
    .then((raw) => {
      const sanitized = sanitizeSvgMarkup(raw);
      if (sanitized) {
        svgMarkupCache.set(src, sanitized);
      }
      svgMarkupPromises.delete(src);
      return sanitized ?? null;
    })
    .catch((error) => {
      console.warn('[PRE-LOAD SYSTEM] Failed to preload tintable SVG', src, error);
      svgMarkupPromises.delete(src);
      return null;
    });

  svgMarkupPromises.set(src, promise);
  return promise;
}

export function getTintableSvgMarkup(src) {
  return svgMarkupCache.get(src) ?? null;
}

export function useTintableSvg(src) {
  const [markup, setMarkup] = useState(() => (src ? getTintableSvgMarkup(src) : null));

  useEffect(() => {
    if (!src) {
      return undefined;
    }

    if (svgMarkupCache.has(src)) {
      setMarkup(svgMarkupCache.get(src) ?? null);
      return undefined;
    }

    let cancelled = false;
    preloadTintableSvg(src).then((result) => {
      if (!cancelled && result) {
        setMarkup(result);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [src]);

  return markup;
}

export function clearTintableSvgCache() {
  tintableSvgSet.clear();
  svgMarkupCache.clear();
  svgMarkupPromises.clear();
}
